import { z } from "zod";
import type { SceneDocument } from "../scene/schema";

/**
 * Working together (docs/15-zusammenarbeit.md): comments and share links that
 * open a file without an account.
 */

/** What a share link allows. */
export const SHARE_ACCESS = ["view", "comment"] as const;
export type ShareAccess = (typeof SHARE_ACCESS)[number];

export const SHARE_ACCESS_LABELS: Record<ShareAccess, string> = {
  view: "Nur ansehen",
  comment: "Ansehen & kommentieren",
};

export const COMMENT_MAX_LENGTH = 5000;
/** Name guests give when they comment through a link. */
export const GUEST_NAME_MAX_LENGTH = 80;

export const commentSchema = z.object({
  id: z.uuid(),
  /** First comment of the thread (replies); null = starts a thread. */
  parentId: z.uuid().nullable(),
  /** Page the comment belongs to; with `x`/`y` a pin on it (document px). */
  pageId: z.string().nullable(),
  elementId: z.string().nullable(),
  x: z.number().nullable(),
  y: z.number().nullable(),
  /** Videos: moment on the timeline. */
  timeMs: z.number().nullable(),
  body: z.string(),
  author: z.object({
    id: z.string().nullable(),
    name: z.string(),
    /** Written through a share link, without an account. */
    guest: z.boolean(),
  }),
  resolvedAt: z.date().nullable(),
  resolvedBy: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  /** The caller wrote it (may edit and delete it). */
  mine: z.boolean(),
});
export type DocumentComment = z.infer<typeof commentSchema>;

/** Where a new comment sits. */
export const commentAnchorSchema = z.object({
  parentId: z.uuid().optional(),
  pageId: z.string().max(64).optional(),
  elementId: z.string().max(64).optional(),
  x: z.number().min(-100_000).max(100_000).optional(),
  y: z.number().min(-100_000).max(100_000).optional(),
  timeMs: z
    .number()
    .int()
    .min(0)
    .max(24 * 60 * 60 * 1000)
    .optional(),
});

export const commentBodySchema = z
  .string()
  .trim()
  .min(1, "Bitte einen Kommentar schreiben")
  .max(COMMENT_MAX_LENGTH, `Höchstens ${COMMENT_MAX_LENGTH} Zeichen`);

export const guestNameSchema = z
  .string()
  .trim()
  .min(1, "Bitte gib deinen Namen an")
  .max(GUEST_NAME_MAX_LENGTH);

export const shareLinkSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
  /** Full URL to hand out (/s/<token>). */
  url: z.string(),
  label: z.string().nullable(),
  access: z.enum(SHARE_ACCESS),
  expiresAt: z.date().nullable(),
  revokedAt: z.date().nullable(),
  viewCount: z.number(),
  lastViewedAt: z.date().nullable(),
  createdAt: z.date(),
  createdBy: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type ShareLink = z.infer<typeof shareLinkSchema>;

export const shareTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{20,100}$/, "Ungültiger Link");

/** Everything the public share page needs (no account). */
export const shareViewSchema = z.object({
  title: z.string(),
  kind: z.enum(["design", "video"]),
  orgName: z.string(),
  access: z.enum(SHARE_ACCESS),
  revision: z.number(),
  updatedAt: z.date(),
  expiresAt: z.date().nullable(),
  /** Brand values resolved. */
  content: z.custom<SceneDocument>(),
  /** Media of the file, URLs carry the link's token. */
  assets: z.record(
    z.string(),
    z.object({
      url: z.string(),
      width: z.number().nullable(),
      height: z.number().nullable(),
    }),
  ),
  fonts: z.array(
    z.object({
      family: z.string(),
      faces: z.array(
        z.object({
          weight: z.number(),
          style: z.enum(["normal", "italic"]),
          url: z.string(),
          unicodeRange: z.string().nullable(),
        }),
      ),
    }),
  ),
  /** Videos: the latest finished MP4 export (the page plays it). */
  video: z.object({ url: z.string(), revision: z.number(), createdAt: z.date() }).nullable(),
  thumbnailUrl: z.string().nullable(),
  /** Only for links that allow comments. */
  comments: z.array(commentSchema),
});
export type ShareView = z.infer<typeof shareViewSchema>;
